import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { toast } from 'react-toastify';
// Helpers
import { colleagueStatus } from 'constants/Helpers';
// SVG
import BackIcon from 'assets/images/svg/BackIcon';
import StarIcon from 'assets/images/svg/StarIcon';
import WarningIcon from 'assets/images/svg/WarningIcon';
// Components
import ChangeStatusBtn from 'components/orders/ChangeStatusBtn';
import CustomButton from 'components/common/CustomButton';
// Actions
import { setOrderDetailsAllData } from 'store/orderDetails/OrderDetailsActions';
// API
import OrderDetailsService from 'services/OrderDetailsService';
import OrdersListService from 'services/OrdersListService';

const NEEDS_PREP = 1;

/**
 * Sticky identity + actions bar at the top of the order details page. Everything it
 * does - pause/resume, status changes, the prepared-once disclaimer - is
 * the flow the old OrderDetailsCard carried; only the layout is new.
 */
const OrderDetailsTopBar = ({
  orderDetails,
  cartDetails,
  hasSubmittedBefore,
  hasDeviation,
  showPause = false,
  preparationTasks = null,
  refreshPreparationTasks,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isPaused = orderDetails?.preparationStatus === 9;
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState(null);
  const [pauseReasons, setPauseReasons] = useState([]);
  const [isPauseLoading, setIsPauseLoading] = useState(false);
  const editDeviationEnabled = true;

  // The store seeds cartDetails with '' before the order loads.
  const tasks = Array.isArray(cartDetails) ? cartDetails : [];
  const tasksCount = tasks.length;
  const needsPrepCount = tasks.filter((item) => item?.preparationType === NEEDS_PREP).length;

  useEffect(() => {
    const fetchPauseReasons = async () => {
      try {
        const res = await OrderDetailsService.getPauseReasons();
        setPauseReasons(res?.data ?? []);
      } catch (err) {
        setPauseReasons([]);
      }
    };
    // Only the pause modal needs these, and it can no longer be opened.
    if (showPause && isPauseModalOpen) {
      fetchPauseReasons();
    }
  }, [showPause, isPauseModalOpen]);

  // Order-level actions move the SKU tasks too - Not done resets every started, paused
  // and finished task back to Start-ready - so the task list is re-read alongside the
  // order rather than waiting for a page reload.
  const fetchOrderDetails = (orderId) => {
    dispatch(setOrderDetailsAllData(orderId));
    if (refreshPreparationTasks) {
      refreshPreparationTasks();
    }
  };

  const handlePauseClick = async () => {
    if (isPaused) {
      setIsPauseLoading(true);
      try {
        const res = await OrdersListService.changeOrderPreparationStatus(
          { status: 2 },
          orderDetails?.id
        );
        toast.success(res?.data?.message ?? 'Order resumed successfully');
        fetchOrderDetails(orderDetails?.id);
      } catch (err) {
        toast.error(err?.response?.data?.message ?? 'Failed to resume order');
      } finally {
        setIsPauseLoading(false);
      }
    } else {
      setPauseReason(null);
      setIsPauseModalOpen(true);
    }
  };

  const handlePauseOrder = async () => {
    setIsPauseLoading(true);
    try {
      const res = await OrdersListService.changeOrderPreparationStatus(
        { pauseReasonId: parseInt(pauseReason, 10), status: 9 },
        orderDetails?.id
      );
      toast.success(res?.data?.message ?? 'Order paused successfully');
      setIsPauseModalOpen(false);
      fetchOrderDetails(orderDetails?.id);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Failed to pause order');
    } finally {
      setIsPauseLoading(false);
    }
  };

  return (
    <>
      <div className="order-details-topbar">
        <div className="topbar-left">
          <button
            type="button"
            className="topbar-back"
            onClick={() => navigate(-1)}
            title="Back"
          >
            <BackIcon />
          </button>

          <div className="topbar-field">
            <span className="topbar-label">Tasks</span>
            <span className="topbar-value tasks-value">
              <span className="tasks-dot" />
              {tasksCount}
              {needsPrepCount > 0 && <small>({needsPrepCount} need prep)</small>}
            </span>
          </div>

          <div className="topbar-field order-field">
            <span className="topbar-order-id">#{orderDetails?.id}</span>
            <span className="topbar-order-tags">
              {orderDetails?.rank != null && (
                <span
                  className="rank-badge"
                  title={
                    orderDetails?.fulfillmentTimeDisplay
                      ? `Fulfillment time: ${orderDetails?.fulfillmentTimeDisplay}`
                      : undefined
                  }
                >
                  Rank #{orderDetails.rank}
                </span>
              )}
              {orderDetails?.productionLine === 1 && <StarIcon cssClass="star-icon" />}
              {orderDetails?.isNwDelivery ? <span className="nw-order">NW</span> : null}
              {orderDetails?.orderType === 10 && orderDetails?.partnerName === 'Talabat' && (
                <span className="talabat-tag">Talabat</span>
              )}
              {(orderDetails?.firstOrder === 0 || orderDetails?.firstOrder === 2) && (
                <span className="first-order-tag sender">1st-S</span>
              )}
              {(orderDetails?.firstOrder === 1 || orderDetails?.firstOrder === 2) && (
                <span className="first-order-tag recipient">1st-R</span>
              )}
            </span>
          </div>

          <div className="topbar-field">
            <span className="topbar-label">Status</span>
            <span className={`topbar-status status-${orderDetails?.preparationStatus ?? 'none'}`}>
              {colleagueStatus[orderDetails?.preparationStatus] || 'Not Assigned'}
            </span>
          </div>

          <div className="topbar-field">
            <span className="topbar-label">Assigned by</span>
            <span className="topbar-value">{orderDetails?.floristAssignedByDto?.name || '-'}</span>
          </div>

          <div className="topbar-field">
            <span className="topbar-label">Assigned To</span>
            <span className="topbar-value">{orderDetails?.floristDto?.name || '-'}</span>
          </div>
        </div>

        <div className="topbar-actions">
          {/* Pause is per task now, so the order-level Pause is hidden. Resume stays:
              a paused order shows no Done button, so it would have no way forward. */}
          {showPause && isPaused && (
            <CustomButton
              id="pause-resume-btn"
              name="▷ Resume"
              handleClick={handlePauseClick}
              type="status"
              cssClasses="pause-resume-btn resume-btn"
              disabled={isPauseLoading}
            />
          )}
          <ChangeStatusBtn
            item={orderDetails}
            fetchMoreData={fetchOrderDetails}
            details={true}
            showAllCTA={true}
            hasSubmittedBefore={hasSubmittedBefore}
            hasDeviation={hasDeviation}
            hideDone={isPaused}
            preparationTasks={preparationTasks}
          />
        </div>
      </div>

      {orderDetails?.note ? (
        <div className="order-details-note">
          <b>Note:</b> {orderDetails.note}
        </div>
      ) : null}

      {editDeviationEnabled &&
        orderDetails?.preparedPreviously &&
        orderDetails?.preparationStatus !== 0 && (
          <div className="header-disclaimer order-details-disclaimer">
            <WarningIcon />{' '}
            <p>This order was prepared once. Check with supervisor before moving ahead.</p>
          </div>
        )}

      <Modal
        isOpen={isPauseModalOpen}
        toggle={() => setIsPauseModalOpen(false)}
        className="pause-order-modal"
        centered={true}
      >
        <ModalHeader
          close={
            <span className="pause-modal-close" onClick={() => setIsPauseModalOpen(false)}>
              &times;
            </span>
          }
        >
          Pause Order Preparation
        </ModalHeader>
        <ModalBody>
          <p className="pause-modal-description">
            Select a reason for pausing this order. You can work on other orders while this one is
            paused.
          </p>
          <div className="pause-reason-select">
            <label>Pause Reason</label>
            <select value={pauseReason ?? ''} onChange={(e) => setPauseReason(e.target.value)}>
              <option value="" disabled>
                Select a reason
              </option>
              {pauseReasons.map((reason) => (
                <option key={reason.id} value={reason.id}>
                  {reason.reason}
                </option>
              ))}
            </select>
          </div>
        </ModalBody>
        <ModalFooter className="pause-modal-footer">
          <CustomButton
            id="pause-cancel-btn"
            name="Cancel"
            handleClick={() => setIsPauseModalOpen(false)}
            type="cancel"
            cssClasses="pause-cancel-btn"
          />
          <CustomButton
            id="pause-confirm-btn"
            name="Pause Order"
            handleClick={handlePauseOrder}
            type="proceed"
            cssClasses="pause-confirm-btn"
            disabled={!pauseReason || isPauseLoading}
          />
        </ModalFooter>
      </Modal>
    </>
  );
};

export default OrderDetailsTopBar;
